import styled from '@emotion/styled';
import { css, SerializedStyles } from '@emotion/react';
import { withConfig } from 'src/utils/withConfig';
import { ThemeColorsType } from 'src/domains/common/themeColors';

interface TraderChatTextAreaPropsType {
    height: string;
}

interface TraderChatMessagesListPropsType {
    hasAssignee?: boolean;
}

interface TraderChatButtonsPropsType {
    notPermitted?: boolean;
}

export const TraderChatTextArea = withConfig(theme => styled('textarea', { label: 'TraderChatTextArea' })<TraderChatTextAreaPropsType>`
    box-sizing: border-box;
    height: ${({ height }): string => height};
    width: 100%;
    max-height: 200px;
    min-height: 64px;
    border: 2px solid ${theme.traderChat.borderColor};
    font-family: inherit;
    background: transparent;
    padding: 4px 64px 4px 10px;
    line-height: 1.4;
    font-weight: 600;
    resize: none;
    &:hover,
    &:focus {
        border-color: ${theme.traderChat.borderColor};
        outline: none;
    }
`);

export const TraderChatInputWrapper = styled('div', { label: 'TraderChatInputWrapper' })`
    position: relative;
    border-radius: 3px;
    margin: 5px 0;
`;

export const TraderChatButton = withConfig(theme => styled('button', { label: 'TraderChatButton' })`
    border-radius: 3px;
    border: 1px solid ${theme.traderChat.borderColorSecondary};
    background: transparent;
    font-weight: bold;
    font-size: 12px;
    color: ${theme.traderChat.txtColor};
    width: 100%;
    padding: 8px 12px;
    cursor: pointer;
    &:hover {
        background: rgba(0, 0, 0, 0.05);
    }
`);

export const TraderChatButtonsWrapper = styled('div', { label: 'TraderChatButtonsWrapper' })`
    display: flex;
    gap: 6px;
`;

export const TraderChatSendButton = styled(TraderChatButton, { label: 'TraderChatSendButton' })`
    position: absolute;
    right: 4px;
    bottom: 12px;
    width: auto;
`;

export const TraderChatWrapper = styled('section', { label: 'TraderChatWrapper' })`
    font-size: 13.5px;
    height: 100%;
    min-width: 350px;
    display: flex;
    flex-flow: column nowrap;
`;

export const TraderChatAssignee = withConfig(theme => styled('div', { label: 'TraderChatAssignee' })`
    display: block;
    text-align: center;
    border-bottom: 1px solid ${theme.traderChat.borderColor};
    padding: 10px 0;
`);

export const TraderChatMessagesList = styled('ul', { label: 'TraderChatMessagesList' })<TraderChatMessagesListPropsType>`
    list-style-type: none;
    margin: 0;
    padding: 10px 20px;
    height: ${({ hasAssignee }): string => (hasAssignee === true) ? 'calc(100% - 121px)' : 'calc(100% - 78px)'};
    overflow: auto;
`;


interface TraderChatMessageItemPropsType {
    ownMessage: boolean;
}

const setTraderChatMessageItem = (ownMessage: boolean, theme: ThemeColorsType): SerializedStyles => {
    if (ownMessage) {
        return css`
            background: ${theme.traderChat.bgColor};
            &::before {
                border-width: 10px 15px 0 0;
                left: 100%;
                border-color: ${theme.traderChat.bgColor} transparent transparent transparent;
            }

            &::after {
                left: 100%;
                border-width: 12px 17px 0 0;
                border-color: ${theme.traderChat.borderColor} transparent transparent transparent;
            }
        `;
    }
    return css`
        background: ${theme.traderChat.borderColor};

        &::before {
            border-width: 0 15px 10px 0;
            border-color: transparent ${theme.traderChat.borderColor} transparent transparent;
            left: auto;
            right: 100%;
        }

        &::after {
            border-width: 0 17px 12px 0;
            border-color: transparent ${theme.traderChat.borderColor} transparent transparent;
            left: auto;
            right: 100%;
        }
    `;
};

export const TraderChatMessageItem = withConfig(theme => styled('li', { label: 'TraderChatMessageItem' })<TraderChatMessageItemPropsType>`
    position: relative;
    border-radius: 3px;
    border: 1px solid ${theme.traderChat.borderColor};
    box-shadow: 0 0 10px 0 rgba(214, 230, 242, 0.3);
    padding: 10px;
    margin: 10px 0;
    word-break: break-word;
    white-space: pre-wrap;
    ${({ ownMessage }): SerializedStyles => setTraderChatMessageItem(ownMessage, theme)};

    &::before {
        content: '';
        position: absolute;
        top: 0;
        z-index: 2;
        margin-left: -1px;
        width: 0;
        height: 0;
        border-style: solid;
    }

    &::after {
        content: '';
        position: absolute;
        top: -1px;
        z-index: 1;
        width: 0;
        height: 0;
        border-style: solid;
    }
`);

export const TraderChatMessageFooter = styled('div', { label: 'TraderChatMessageFooter' })`
    display: flex;
    align-items: center;
    margin-top: 5px;
    gap: 8px;
`;

export const TraderChatMessageAuthor = styled('div', { label: 'TraderChatMessageAuthor' })`
    font-size: 10.5px;
    font-weight: bold;
    opacity: 0.5;

    span {
        font-weight: 400;
    }
`;

export const TraderChatButtons = styled('div', { label: 'TraderChatButtons' })<TraderChatButtonsPropsType>`
    width: 100%;
`;

export const TraderChatDeleteButton = styled('button', { label: 'TraderChatDeleteButton' })`
    background: transparent;
    border: none;
    cursor: pointer;
    opacity: 0.4;
    padding: 0;
    margin-left: auto;
    line-height: 1;
    font-size: 12px;
    flex-shrink: 0;
    &:hover {
        opacity: 1;
    }
`;
